import * as Sentry from "@sentry/react";

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  [key: string]: unknown;
}

const isDevelopment = import.meta.env.DEV;

class Logger {
  private formatMessage(level: LogLevel, message: string, context?: LogContext) {
    const timestamp = new Date().toISOString();
    const contextString = context ? `\nContext: ${JSON.stringify(context, null, 2)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextString}`;
  }

  private logToConsole(level: LogLevel, message: string, context?: LogContext) {
    const styles = {
      info: 'color: #00b8d4; font-weight: bold;',
      warn: 'color: #ff9800; font-weight: bold;',
      error: 'color: #f44336; font-weight: bold;',
      debug: 'color: #9e9e9e; font-weight: bold;',
    };

    const emoji = {
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      debug: '🐛',
    };

    console.groupCollapsed(`%c${emoji[level]} ${message}`, styles[level]);
    if (context) console.log('Context:', context);
    console.trace('Stack Trace');
    console.groupEnd();
  }

  private logToSentry(level: LogLevel, message: string, context?: LogContext) {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setExtras(context);
      }
      
      const sentryLevel = level === 'warn' ? 'warning' : level === 'error' ? 'error' : 'info';
      scope.setLevel(sentryLevel);

      // Breadcrumbs ajudam a entender o que aconteceu antes de um erro
      Sentry.addBreadcrumb({
        category: 'app',
        message,
        level: sentryLevel,
        data: context,
      });

      // Se for erro, envia o evento completo
      if (level === 'error') {
        Sentry.captureMessage(message, sentryLevel);
      }
    });
  }

  public info(message: string, context?: LogContext) {
    if (isDevelopment) {
      this.logToConsole('info', message, context);
    }
    this.logToSentry('info', message, context);
  }

  public warn(message: string, context?: LogContext) {
    if (isDevelopment) {
      this.logToConsole('warn', message, context);
    }
    this.logToSentry('warn', message, context);
  }

  public error(message: string, error?: unknown, context?: LogContext) {
    const errorContext = {
      ...context,
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    };

    if (isDevelopment) {
      this.logToConsole('error', message, errorContext);
    }
    
    // Para erros, capturamos a exceção real se disponível
    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: context,
      });
    } else {
      this.logToSentry('error', message, errorContext);
    }
  }

  public debug(message: string, context?: LogContext) {
    if (isDevelopment) {
      this.logToConsole('debug', message, context);
    }
    // Debug logs geralmente não vão para o Sentry em produção para economizar cota
    // mas podem ser úteis como breadcrumbs de nível 'debug'
    Sentry.addBreadcrumb({
      category: 'debug',
      message,
      level: 'debug',
      data: context,
    });
  }
}

export const logger = new Logger();
